import React, {
  Fragment,
  useEffect,
  useState
} from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import {
  Button,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import { fetchAllData } from "../../../dataProvider";
import { delay } from "utils/tools";
import TableResultConfirmAdhocFees from "./TableResultConfirmAdhocFees";
import SaveIcon from "@material-ui/icons/Save";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

interface IApproveAllButton {
  schoolId?: number;
  subcontractorId?: number;
}

interface IApproveResults {
  id: number;
  status: string;
}

const ApproveAllButton = ({ schoolId, subcontractorId }: IApproveAllButton) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IApproveResults[]>([]);
  const [processing, setProcessing] = useState(false);
  const [school, setSchool] = useState<ISchool>();
  const [subcontractor, setSubcontractor] = useState<ISubcontractor>();

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  /**
   * Get the school details if `schoolId` is present.
   * This is used to personalize the dialog title/message.
   */
  useEffect(() => {
    if (schoolId) {
      provider
        .getOne("schools", {
          id: schoolId,
        })
        .then((res: any) => {
          if (res && res.data) {
            setSchool(res.data as ISchool);
          }
        })
        .catch((err) => console.error("Failed to fetch school info:", err));
    } else {
      setSchool(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  /**
   * Get the subcontractor details if `subcontractorId` is present.
   * This is used to personalize the dialog title/message.
   */
  useEffect(() => {
    if (subcontractorId) {
      provider
        .getOne("subcontractors", {
          id: subcontractorId,
        })
        .then((res: any) => {
          if (res && res.data) {
            setSubcontractor(res.data as ISubcontractor);
          }
        })
        .catch((err) =>
          console.error("Failed to fetch subcontractor info:", err),
        );
    } else {
      setSubcontractor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorId]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("subcontractorAdhocFees");
      refresh();
    }

    setOpen(false);
  };

  /**
   * Approve an individual adhoc fee record by ID.
   * This also updates the UI with success/failure status for each row.
   */
  const handleApprove = (
    selectedAdhocFee: IApproveResults,
    adhocFees: IApproveResults[],
    index: number,
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customUpdate(
          `subcontractorAdhocFees/${selectedAdhocFee.id}/approve`,
          {
            data: {},
          },
        )
        .then(() => {
          adhocFees[index].status = "Approved";
          setResults([...adhocFees]);

          resolve(true);
        })
        .catch((err: any) => {
          adhocFees[index].status = `Error, ${err.message}`;
          setResults([...adhocFees]);

          resolve(false);
        });
    });
  };

  /**
   * Get all pending items based on filters, then approves them one by one.
   * Result will show in the table for each data.
   */
  const handleConfirm = async () => {
    setProcessing(true);

    // get all adhoc fees matching the filters
    const adhocFeesData = await fetchAllData(
      "subcontractorAdhocFees",
      {
        SchoolId: schoolId,
        SubcontractorId: subcontractorId,
        HasIssued: false,
        Status: "Pending",
      },
    );

    const mappedAdhocFees = adhocFeesData.map((data) => ({
      id: data.id,
      status: data.status,
    }));

    // Loop over each pending and approve it sequentially
    for (let i = 0; i < mappedAdhocFees.length; i++) {
      mappedAdhocFees[i].status = "Loading...";
      setResults([...mappedAdhocFees]);

      await handleApprove(mappedAdhocFees[i], mappedAdhocFees, i);
      await delay(2000);
    }

    setFinished(true);
    setProcessing(false);
  };

  /**
   * Helper to dynamically generate the dialog title suffix
   * depending on which filters are present.
   * e.g. "for XYZ Subcontractor in ABC School"
   */
  const suffix = `${subcontractor?.name ? ` for ${subcontractor.name}` : ""}${
    school?.name ? ` in ${school.name}` : ""
  }`;

  return (
    <Fragment>
      <Button
        label="Approve All"
        onClick={handleOpen}
        style={{ backgroundColor: "#ffbf00" }}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          {`Approve All The Pending Adhoc Fees${suffix}`}
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmAdhocFees records={results} />
          ) : (
            <DialogContentText color="initial">
              {`Are you sure you want to approve all pending adhoc fees${suffix}?`}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={handleConfirm}
              color="primary"
              label={`${processing ? "Processing..." : "Confirm"}`}
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
            className={classes.cancelButton}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ApproveAllButton;
